import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { getFirestore, doc, runTransaction, collection, addDoc, getDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyCQ4_TkOu4i5H1j43E4dAG7Yv_6vgZ1P68",
  authDomain: "nexrise-internal.firebaseapp.com",
  projectId: "nexrise-internal",
  storageBucket: "nexrise-internal.firebasestorage.app",
  messagingSenderId: "493135056072",
  appId: "1:493135056072:web:cc5beedcf77c2999e069fd",
  measurementId: "G-8WN38C2XM3"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export { ref, uploadBytes, getDownloadURL };
export { GoogleAuthProvider, signInWithPopup, signOut };

/**
 * Automated Email Notification Helper
 * Sends an email using the Trigger Email extension by writing to the 'mail' collection.
 */
export const sendEmailNotification = async (db, type, mocData, orderData) => {
  try {
    const customerID = mocData?.customerID || orderData?.customerID;
    if (!customerID) {
      console.warn("Cannot send email: No customerID found.");
      return;
    }

    // 1. Fetch Customer Details
    const custSnap = await getDoc(doc(db, "customers", customerID));
    if (!custSnap.exists()) {
      console.warn("Cannot send email: Customer document not found.");
      return;
    }
    const customer = custSnap.data();
    
    // Priority for email: billing address email -> shipping address email -> customer profile email
    const recipientEmail = orderData?.billingAddress?.email || 
                           orderData?.shippingAddress?.email || 
                           customer.email || 
                           customer.contact_email;

    if (!recipientEmail) {
      console.warn("Cannot send email: No recipient email address found.");
      return;
    }

    // 2. Determine Template
    let templateName = '';
    switch(type) {
      case 'scheduled': templateName = 'packing_scheduled'; break;
      case 'done': templateName = 'packing_done'; break;
      case 'dispatched': templateName = 'dispatch_template'; break;
      default: templateName = type;
    }
    
    // 3. Prepare Dynamic Data
    const trackingUrl = `${window.location.origin}/ops/delivered/${mocData.id || mocData.mocNo}`;
    
    // 4. Prepare Attachments for Dispatch Email
    const attachments = [];
    if (type === 'dispatched') {
      if (mocData.deliveryChallanUrl) {
        attachments.push({
          filename: `Delivery_Challan_${mocData.mocNo || 'MOC'}.pdf`,
          path: mocData.deliveryChallanUrl
        });
      }
      if (mocData.vehicleProofUrl) {
        attachments.push({
          filename: `Vehicle_Proof_${mocData.mocNo || 'MOC'}.jpg`,
          path: mocData.vehicleProofUrl
        });
      }
    }

    const emailData = {
      to: recipientEmail,
      message: {
        attachments: attachments
      },
      template: {
        name: templateName,
        data: {
          customerName: customer.display_name || customer.company_name || customer.name || "Customer",
          orderNumber: orderData?.orderNumber || mocData?.orderNumber || "---",
          mocNo: mocData?.mocNo || "---",
          trackingUrl: trackingUrl,
          // Dispatch Specific Data
          quantityVerifiedByDispatch: mocData.quantityVerifiedByDispatch || "Yes",
          dispatchedThrough: mocData.dispatchedThrough || "---",
          driverName: mocData.driverName || "---",
          driverContact: mocData.driverContact || "---",
          vehicleNumber: mocData.vehicleNumber || "---",
          vehicleProofUrl: mocData.vehicleProofUrl || "",
          deliveryChallanUrl: mocData.deliveryChallanUrl || ""
        }
      }
    };

    // 5. Drop the 'letter' into the 'mail' collection
    await addDoc(collection(db, "mail"), emailData);
    console.log(`Email queued successfully: ${templateName} to ${recipientEmail}`);
    
    return true;
  } catch (error) {
    console.error("Error sending email notification:", error);
    return false;
  }
};

const getFirmPrefix = (firmId) => {
  if (firmId.startsWith('nexrise')) return 'NIIPL';
  if (firmId.startsWith('jagdish')) return 'JE';
  if (firmId.startsWith('cloudnine')) return 'C9';
  return 'GEN';
};

export const getNextVoucherNumber = async (db, firmId) => {
  const firmPrefix = getFirmPrefix(firmId);
  const counterKey = `invoice_${firmPrefix.toLowerCase()}_count`;
  const sequencesRef = doc(db, "counters", "sequences");
  const yearCode = "25-26";

  try {
    const newCounter = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      if (!sequenceDoc.exists()) {
        throw new Error("Counter document does not exist!");
      }
      const currentCount = sequenceDoc.data()[counterKey] || 1000;
      const nextCount = currentCount + 1;
      transaction.update(sequencesRef, { [counterKey]: nextCount });
      return currentCount;
    });

    return `${firmPrefix}/${yearCode}/IN${newCounter}`;
  } catch (error) {
    console.error("Error getting next voucher number: ", error);
    return null;
  }
};

export const getNextMocNumber = async (db) => {
  const sequencesRef = doc(db, "counters", "moc_sequence");

  try {
    const result = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      
      let currentCount = 4900;
      if (sequenceDoc.exists() && sequenceDoc.data().count !== undefined) {
        currentCount = sequenceDoc.data().count;
      }
      
      const nextCount = currentCount + 1;
      transaction.set(sequencesRef, { count: nextCount }, { merge: true });
      return currentCount;
    });

    return result.toString();
  } catch (error) {
    console.error("Error getting next MOC number: ", error);
    return "";
  }
};

export const getNextSequenceNumber = async (db, firmId, typePrefix, counterCollection = "sequences") => {
  const firmPrefix = getFirmPrefix(firmId);
  const counterKey = `${typePrefix.toLowerCase()}_${firmPrefix.toLowerCase()}_count`;
  const sequencesRef = doc(db, "counters", counterCollection);
  const yearCode = new Date().getFullYear().toString().slice(-2); // Current year in YY format

  try {
    const newCounter = await runTransaction(db, async (transaction) => {
      const sequenceDoc = await transaction.get(sequencesRef);
      
      let currentCount = 1000; // Default starting count
      if (sequenceDoc.exists()) {
        currentCount = sequenceDoc.data()[counterKey] || 1000;
      } else {
        // If the main sequences document doesn't exist, create it with initial counts
        transaction.set(sequencesRef, {
          [counterKey]: currentCount
        }, { merge: true }); // Use merge: true to avoid overwriting other counters if doc exists
        return currentCount;
      }
      
      const nextCount = currentCount + 1;
      transaction.update(sequencesRef, { [counterKey]: nextCount });
      return currentCount;
    });

    return `${firmPrefix}/${yearCode}/${typePrefix.toUpperCase()}${newCounter}`;
  } catch (error) {
    console.error(`Error getting next ${typePrefix} number: `, error);
    return null;
  }
};